// /app/frontend/src/components/handshake/DeliveryPhotoCapture.js
// Componente para capturar foto da entrega (pacote + número da casa)
import React, { useState, useRef, useCallback } from 'react';
import { FiCamera, FiX, FiCheck, FiRotateCw, FiUpload, FiAlertTriangle } from 'react-icons/fi';
import api from '@/services/api';

const DeliveryPhotoCapture = ({ orderId, driverLocation, onSuccess, onCancel }) => {
  const [step, setStep] = useState('capture'); // 'capture', 'preview', 'uploading', 'success'
  const [photo, setPhoto] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  // Iniciar câmera
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setError('');
    } catch (err) {
      console.error('Erro ao acessar câmera:', err);
      setError('Não foi possível acessar a câmera. Tente usar o botão "Escolher Foto".');
    }
  }, []);

  // Parar câmera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  // Iniciar câmera ao montar
  React.useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, [startCamera, stopCamera]);

  // Capturar foto da câmera
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);

    canvas.toBlob((blob) => {
      setPhoto(blob);
      setPhotoUrl(canvas.toDataURL('image/jpeg', 0.8));
      setStep('preview');
      stopCamera();
    }, 'image/jpeg', 0.8);
  };

  // Selecionar foto da galeria
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Selecione apenas arquivos de imagem');
      return;
    }

    setPhoto(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setPhotoUrl(e.target.result);
      setStep('preview');
      stopCamera();
    };
    reader.readAsDataURL(file);
  };

  // Tirar nova foto
  const retakePhoto = () => {
    setPhoto(null);
    setPhotoUrl(null);
    setStep('capture');
    startCamera();
  };

  // Fazer upload e finalizar entrega
  const uploadAndComplete = async () => {
    if (!photo || !driverLocation) {
      setError('Foto ou localização não disponível');
      return;
    }

    setUploading(true);
    setError('');

    try {
      // 1. Upload da foto
      const formData = new FormData();
      formData.append('file', photo, 'delivery_photo.jpg');

      const uploadResponse = await api.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const uploadedPhotoUrl = uploadResponse.data.url;

      // 2. Completar entrega
      const completeResponse = await api.post('/delivery/dropoff/complete', {
        order_id: orderId,
        delivery_photo_url: uploadedPhotoUrl,
        driver_location: driverLocation
      });

      setStep('success');
      
      setTimeout(() => {
        onSuccess(completeResponse.data);
      }, 2000);

    } catch (err) {
      console.error('Erro ao finalizar entrega:', err);
      setError(err.response?.data?.detail || 'Erro ao enviar foto. Tente novamente.');
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col" data-testid="delivery-photo-capture">
      {/* Header */}
      <div className="bg-gray-900 p-4 flex items-center justify-between">
        <div>
          <h2 className="text-white font-bold text-lg">📸 Foto de Entrega</h2>
          <p className="text-gray-400 text-sm">Tire uma foto do pacote junto ao número da casa</p>
        </div>
        <button
          onClick={() => { stopCamera(); onCancel(); }}
          className="p-2 text-white hover:bg-white/10 rounded-full"
          data-testid="close-photo-capture"
        >
          <FiX size={24} />
        </button>
      </div>

      {/* Erro */}
      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-900/80 border border-red-500 rounded-xl flex items-center gap-2">
          <FiAlertTriangle className="text-red-400 flex-shrink-0" />
          <span className="text-red-200 text-sm">{error}</span>
        </div>
      )}

      {/* Conteúdo Principal */}
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {step === 'capture' && (
          <>
            {/* Área da câmera */}
            <div className="relative w-full max-w-md aspect-[4/3] bg-gray-800 rounded-2xl overflow-hidden mb-4">
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                playsInline
                muted
              />
              <canvas ref={canvasRef} className="hidden" />
              
              {/* Guia de enquadramento */}
              <div className="absolute inset-4 border-2 border-white/30 rounded-xl pointer-events-none">
                <div className="absolute top-2 left-2 right-2 text-center">
                  <span className="bg-black/50 text-white text-xs px-2 py-1 rounded">
                    Enquadre o pacote e o número da casa
                  </span>
                </div>
              </div>
            </div>

            {/* Dicas */}
            <div className="text-center mb-6">
              <p className="text-gray-400 text-sm mb-2">A foto serve como comprovante de entrega</p>
              <ul className="text-gray-500 text-xs space-y-1">
                <li>✓ Pacote visível na foto</li>
                <li>✓ Número da casa legível</li>
                <li>✓ Foto com boa iluminação</li>
              </ul>
            </div>

            {/* Botões */}
            <div className="flex gap-4 w-full max-w-md">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex-1 py-4 bg-gray-700 text-white rounded-xl font-medium flex items-center justify-center gap-2"
              >
                <FiUpload />
                Galeria
              </button>
              <button
                onClick={capturePhoto}
                className="flex-[2] py-4 bg-green-600 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2 hover:bg-green-700"
                data-testid="capture-photo-btn"
              >
                <FiCamera size={24} />
                Tirar Foto
              </button>
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileSelect}
              className="hidden"
            />
          </>
        )}

        {step === 'preview' && (
          <>
            {/* Preview da foto */}
            <div className="relative w-full max-w-md aspect-[4/3] bg-gray-800 rounded-2xl overflow-hidden mb-4">
              <img
                src={photoUrl}
                alt="Preview da entrega"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-2 right-2">
                <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                  ✓ Foto capturada
                </span>
              </div>
            </div>

            <p className="text-gray-400 text-sm mb-6 text-center">
              Verifique se a foto está nítida e mostra o pacote junto ao número da casa
            </p>

            {/* Botões */}
            <div className="flex gap-4 w-full max-w-md">
              <button
                onClick={retakePhoto}
                disabled={uploading}
                className="flex-1 py-4 bg-gray-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <FiRotateCw />
                Nova Foto
              </button>
              <button
                onClick={uploadAndComplete}
                disabled={uploading}
                className="flex-[2] py-4 bg-green-600 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2 hover:bg-green-700 disabled:opacity-50"
                data-testid="confirm-delivery-photo-btn"
              >
                {uploading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <FiCheck size={24} />
                    Confirmar Entrega
                  </>
                )}
              </button>
            </div>
          </>
        )}

        {step === 'success' && (
          <div className="text-center">
            <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <FiCheck className="text-white text-5xl" />
            </div>
            <h3 className="text-white text-2xl font-bold mb-2">Entrega Concluída!</h3>
            <p className="text-gray-400">
              A foto foi salva e a entrega foi registrada com sucesso.
            </p>
          </div>
        )}
      </div>

      {/* Localização atual */}
      {driverLocation && step !== 'success' && (
        <div className="bg-gray-900 p-3 text-center">
          <p className="text-gray-500 text-xs">
            📍 Sua localização: {driverLocation.lat.toFixed(5)}, {driverLocation.lng.toFixed(5)}
          </p>
        </div>
      )}
    </div>
  );
};

export default DeliveryPhotoCapture;
